import axios from 'axios';
import { apiUrl } from '@/api/config';
import { getAuthToken } from '@/utils/auth';

// Axios instance configured for school admin requests against the school-admin API namespace.
// This provides school-scoped access to users, divisions, and school details.
const adminApi = axios.create({
  baseURL: apiUrl('superadmin/school-admin/'),
  timeout: 60000,
});

adminApi.interceptors.request.use((config) => {
  const token = getAuthToken('admin');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const adminUsersApi = {
  list: (params = {}) => adminApi.get('users/', { params }),
  get: (id) => adminApi.get(`users/${id}/`),
  createStudent: (data) => adminApi.post('users/', { ...data, user_type: 'student' }),
  createTeacher: (data) => adminApi.post('users/', { ...data, user_type: 'teacher' }),
  update: (id, data) => adminApi.put(`users/${id}/`, data),
  toggleActive: (id, data) => adminApi.post(`users/${id}/toggle_active/`, data),
  resetPassword: (id) => adminApi.post(`users/${id}/reset_password/`),
  getAssignments: (id) => adminApi.get(`users/${id}/assignments/`),
  setAssignments: (id, data) => adminApi.post(`users/${id}/set_assignments/`, data),
  delete: (id) => adminApi.delete(`users/${id}/`),
  bulkImport: (userType, file) => {
    const formData = new FormData();
    formData.append('user_type', userType);
    formData.append('file', file);
    return adminApi.post('users/bulk_import/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadSample: (userType) => adminApi.get('users/bulk_import/sample/', {
    params: { user_type: userType },
    responseType: 'blob',
  }),
};

export const adminSchoolApi = {
  current: () => adminApi.get('school/current/'),
  updateProfile: (data) => adminApi.patch('school/update_profile/', data, {
    headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
  }),
  toggleAutoRenew: (data) => adminApi.post('school/toggle_auto_renew/', data),
};

export const adminGradesApi = {
  list: (params = {}) => adminApi.get('grades/', { params }),
  listAll: () => adminApi.get('grades/', { params: { all: true } }),
  remove: (id) => adminApi.delete(`grades/${id}/remove/`),
};

export const adminBoardsApi = {
  listAll: () => adminApi.get('boards/'),
};

export const adminDivisionsApi = {
  list: (params = {}) => adminApi.get('divisions/', { params }),
  create: (data) => adminApi.post('divisions/', data),
  update: (id, data) => adminApi.put(`divisions/${id}/`, data),
  delete: (id) => adminApi.delete(`divisions/${id}/`),
};
